import { TextInput } from "@/components/forms/FormElements";
import { LoadingIcon } from "@/components/Loading";
import Modal from "@/components/ui/Modal";
import { fetchEvents } from "@/utils/eventService";
import { Form, Formik } from "formik";
import { useEffect, useState } from "react";
import Flatpickr from "react-flatpickr";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import * as Yup from "yup";

import EventImageUploadInput from "./EventImageUpload";
import { resetMessage, toggleAddModal } from "./store";

const AddEvent = ({ onFormSubmit }) => {
  const { openEventModal, message } = useSelector((state) => state.event);
  const dispatch = useDispatch();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const [isLoading, setIsLoading] = useState(false);

  // helper: format local time as "YYYY-MM-DD HH:mm"
  const formatLocalDateTime = (d) => {
    const pad = (n) => String(n).padStart(2, "0");
    const yyyy = d.getFullYear();
    const mm = pad(d.getMonth() + 1);
    const dd = pad(d.getDate());
    const hh = pad(d.getHours());
    const mi = pad(d.getMinutes());
    return `${yyyy}-${mm}-${dd} ${hh}:${mi}`;
  };

  const FormValidationSchema = Yup.object({
    title: Yup.string().required("Title is required"),
    amount: Yup.number().required("Amount is required"),
    image: Yup.string(),
    resultsVisibility: Yup.boolean().required("Result visibility is required"),
    voteDisplayOption: Yup.string()
      .oneOf(["card", "table", "bar_chart"], "Invalid display option")
      .required("Vote display option is required"),
    startAt: Yup.date()
      .required("Start date & time is required")
      .min(today, "Start must be today or later"),
    endAt: Yup.date()
      .required("End date & time is required")
      .min(
        Yup.ref("startAt"),
        "End date and time must be greater than start date and time"
      ),
  });

  useEffect(() => {
    if (message) {
      toast.success(message);
      dispatch(fetchEvents());
      dispatch(toggleAddModal(false));
      dispatch(resetMessage());
    }
  }, [message]);

  const handleSubmitEvent = async (values, { resetForm }) => {
    setIsLoading(true);

    const formData = {
      ...values,
      costPerVote: +values.amount,
      startDate: formatLocalDateTime(values.startAt),
      endDate: formatLocalDateTime(values.endAt),
      paymentMethods: ["1"],
      voteDisplayOption: values.voteDisplayOption || "card",
    };

    await onFormSubmit(formData);
    setIsLoading(false);
    resetForm();
  };

  return (
    <Modal
      title="Create Event"
      labelclassName="btn-outline-dark"
      activeModal={openEventModal}
      onClose={() => {
        dispatch(toggleAddModal(false));
        dispatch(fetchEvents());
      }}
    >
      {isLoading && <LoadingIcon />}
      <Formik
        initialValues={{
          title: "",
          amount: "",
          image: "",
          resultsVisibility: true,
          sortByVotes: false,
          voteDisplayOption: "card",
          startAt: today, // single datetime
          endAt: new Date(today.getTime() + 60 * 60 * 1000), // default +1h
        }}
        validationSchema={FormValidationSchema}
        onSubmit={handleSubmitEvent}
      >
        {({ setFieldValue, errors, touched, values, isValid }) => (
          <Form className="space-y-4">
            <TextInput
              name="title"
              label="Event Name"
              placeholder="Event Name"
              required
            />

            <TextInput
              name="amount"
              type="number"
              min="0.5"
              step="0.5"
              label="Event Amount"
              placeholder="Event Amount"
              required
            />

            <EventImageUploadInput
              label="Image"
              name="image"
              setFieldValue={setFieldValue}
              error={errors.image && touched.image ? errors.image : ""}
            />

            <div className="my-6">
              <label htmlFor="resultsVisibility">
                Display Vote Results on website{"  "}
              </label>
              <input
                type="checkbox"
                name="resultsVisibility"
                id="resultsVisibility"
                checked={values.resultsVisibility}
                onChange={(e) =>
                  setFieldValue("resultsVisibility", e.target.checked)
                }
              />
              {errors.resultsVisibility && touched.resultsVisibility && (
                <p className="text-red-500">{errors.resultsVisibility}</p>
              )}
            </div>

            <div className="my-6">
              <label htmlFor="sortByVotes">Sort Nominees by Votes </label>
              <input
                type="checkbox"
                name="sortByVotes"
                id="sortByVotes"
                checked={values.sortByVotes}
                onChange={(e) => setFieldValue("sortByVotes", e.target.checked)}
              />
              {errors.sortByVotes && touched.sortByVotes && (
                <p className="text-red-500">{errors.sortByVotes}</p>
              )}
            </div>

            <div className="my-6">
              <label
                htmlFor="voteDisplayOption"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Display Votes As
              </label>
              <select
                name="voteDisplayOption"
                id="voteDisplayOption"
                className="form-control py-2"
                value={values.voteDisplayOption}
                onChange={(e) =>
                  setFieldValue("voteDisplayOption", e.target.value)
                }
              >
                <option value="card">Card</option>
                <option value="table">Table</option>
                <option value="bar_chart">Bar Chart</option>
              </select>
              {errors.voteDisplayOption && touched.voteDisplayOption && (
                <p className="text-red-500 mt-1">{errors.voteDisplayOption}</p>
              )}
            </div>

            {/* Combined DateTimes with AM/PM */}
            <div className="grid lg:grid-cols-2 gap-4 grid-cols-1">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Start (Date & Time)
                </label>
                <Flatpickr
                  className="form-control py-2"
                  name="startAt"
                  value={values.startAt}
                  onChange={(date) => setFieldValue("startAt", date[0])}
                  options={{
                    enableTime: true,
                    time_24hr: false, // <-- 12-hour clock
                    altInput: true,
                    altFormat: "F j, Y - h:i K", // <-- show AM/PM
                    dateFormat: "Y-m-d h:i K", // <-- store AM/PM in input
                    minuteIncrement: 1,
                    minDate: today,
                  }}
                />
                {errors.startAt && (
                  <div className="text-sm text-red-500 mt-2">
                    {errors.startAt}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  End (Date & Time)
                </label>
                <Flatpickr
                  className="form-control py-2"
                  name="endAt"
                  value={values.endAt}
                  onChange={(date) => setFieldValue("endAt", date[0])}
                  options={{
                    enableTime: true,
                    time_24hr: false, // <-- 12-hour clock
                    altInput: true,
                    altFormat: "F j, Y - h:i K", // <-- show AM/PM
                    dateFormat: "Y-m-d h:i K", // <-- store AM/PM in input
                    minuteIncrement: 1,
                    minDate: values.startAt || today,
                  }}
                />
                {errors.endAt && (
                  <div className="text-sm text-red-500 mt-2">
                    {errors.endAt}
                  </div>
                )}
              </div>
            </div>

            <div className="ltr:text-right rtl:text-left">
              <button
                disabled={!isValid || isLoading}
                className={`btn text-center text-white font-bold py-2 px-4 rounded ${
                  isLoading
                    ? "bg-blue-700 cursor-wait"
                    : !isValid
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700"
                }`}
                type="submit"
              >
                <span className="flex justify-between gap-x-4">
                  {isLoading ? "Saving..." : "Save"}
                  {isLoading && <LoadingIcon />}
                </span>
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default AddEvent;
